import React, { useEffect, useState, useRef, memo, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { SendIcon, PaperclipIcon, XIcon, CalendarIcon, DollarSignIcon, ChevronLeftIcon, ChevronRightIcon, MaximizeIcon, MinimizeIcon, MenuIcon, PlusIcon, CheckCircleIcon, UserIcon, StarIcon, TagIcon, ChevronRightIcon as ChevronRight } from 'lucide-react';
import { useSourcing, Message, MessageType } from '../contexts/SourcingContext';
// Mock requirements constant
const MOCK_REQUIREMENTS = `
1. Introduction
This document specifies the requirements for sourcing 500 Apple MacBook laptops to support our operational and business needs. The procurement aims to ensure standardized, reliable, and secure devices for employees, with vendor selection based on compliance, pricing, delivery, and support terms.
---
2. Scope
	•	Quantity: 500 units of Apple MacBooks.
	•	Purpose: To equip staff with standardized laptops that meet organizational requirements for performance, compatibility, and security.
---
3. Technical Requirements
3.1 Hardware
	•	Model: Latest-generation MacBook Air (M-series chip) or MacBook Pro (M-series chip).
	•	Processor: Apple Silicon M2 or higher.
	•	RAM: Minimum 16GB (configurable to 32GB for power users).
	•	Storage: Minimum 512GB SSD (1TB SSD preferred for specified roles).
	•	Display: Retina display, minimum 13-inch.
	•	Battery Life: Minimum 15 hours of normal usage.
	•	Ports: At least 2 x Thunderbolt/USB 4 ports.
	•	Camera & Audio: 1080p FaceTime HD camera, studio-quality mic, and stereo speakers.
3.2 Software
	•	Operating System: macOS (latest stable release).
	•	Licenses: Devices must come with standard macOS pre-installed.
	•	Security: Must support MDM (Mobile Device Management) integration.
3.3 Accessories
	•	Charger: One official Apple charger (USB-C, 67W or equivalent) per unit.
	•	Optional: USB-C to USB-A adapters and protective cases (to be quoted separately).
---
4. Vendor Requirements
	•	Authorized Reseller: Vendor must be an Apple Authorized Reseller or Distributor.
	•	Warranty: Standard Apple 1-year warranty plus option for AppleCare+ (3-year).
	•	Support: Vendor to provide technical support and facilitate warranty claims.
	•	Scalability: Vendor should demonstrate capacity to supply additional units in the future.
	•	Compliance: Devices must be sourced through legal, authorized supply channels.
---
5. Delivery & Logistics
	•	Delivery Schedule: Full batch of 500 units delivered no later than [insert date].
	•	Packaging: Secure and eco-friendly packaging; devices must be sealed and unused.
	•	Tracking: Vendor to provide shipment tracking details.
	•	Insurance: All shipments must be insured until delivery and acceptance.
---
6. Pricing & Payment Terms
	•	Pricing: Vendor to provide unit cost, bulk discount rates, and optional accessories pricing.
	•	Payment Terms: Net 30
	•	Taxes & Duties: Prices must be inclusive/exclusive of applicable taxes and duties (to be specified).
---
7. Acceptance Criteria
	•	Devices must meet or exceed technical specifications.
	•	All 500 units must be delivered, verified, and accepted by IT department.
	•	Vendor must provide proof of Apple Authorized Reseller status.
`;
// Add mock suppliers data
const MOCK_SUPPLIERS = [{
  id: 'supplier-1',
  name: 'TechVision Systems',
  description: 'Global supplier of high-quality electronics and computer hardware.',
  tags: ['Electronics', 'Hardware', 'Global'],
  rating: 4.8,
  selected: false
}, {
  id: 'supplier-2',
  name: 'Quantum Computing Solutions',
  description: 'Specialized in advanced computing hardware and peripherals.',
  tags: ['Computing', 'Hardware', 'Premium'],
  rating: 4.5,
  selected: false
}, {
  id: 'supplier-3',
  name: 'MacroTech Distributors',
  description: 'Large-scale distributor of Apple products with competitive pricing.',
  tags: ['Apple', 'Distributor', 'Competitive'],
  rating: 4.2,
  selected: false
}, {
  id: 'supplier-4',
  name: 'Enterprise IT Solutions',
  description: 'Full-service provider of enterprise IT hardware and support.',
  tags: ['Enterprise', 'Support', 'Hardware'],
  rating: 4.7,
  selected: false
}, {
  id: 'supplier-5',
  name: 'Global Tech Imports',
  description: 'International supplier with extensive Apple product inventory.',
  tags: ['International', 'Apple', 'Bulk'],
  rating: 4.0,
  selected: false
}];
// Add SupplierCard component
import SupplierCard from './SupplierCard';
const SourcingAgent = () => {
  const navigate = useNavigate();
  const {
    sourcingEvent,
    setSourcingEvent,
    createNewEvent,
    updateEventStatus,
    toggleSupplierSelection,
    addSuppliers,
    // Use shared state from context
    messages,
    setMessages,
    isAiTyping,
    setIsAiTyping,
    currentWorkflow,
    setCurrentWorkflow
  } = useSourcing();
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // Add state to track expanded mode
  const [isExpanded, setIsExpanded] = useState(false);
  // Add state for custom requirements input
  const [customRequirements, setCustomRequirements] = useState('');
  // Add state for sidebar visibility
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Initialize selectedSuppliers from context when component mounts
  useEffect(() => {
    if (sourcingEvent && sourcingEvent.suppliers) {
      // Extract IDs of already selected suppliers from context
      const selectedIds = sourcingEvent.suppliers.filter(supplier => supplier.selected).map(supplier => supplier.id);
      console.log('Initializing selectedSuppliers from context:', selectedIds);
      setSelectedSuppliers(selectedIds);
    }
  }, [sourcingEvent]);
  // Add state for selected suppliers
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  // Add handler to toggle expanded mode
  const toggleExpandedMode = () => {
    setIsExpanded(!isExpanded);
  };
  // Calendar navigation functions
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  // Generate calendar days
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };
  const handleDateSelection = day => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(newDate);
  };
  const confirmDateSelection = () => {
    handleCalendarSelect(selectedDate);
    setShowCalendarModal(false);
  };
  // Scroll to bottom when messages change, but only within the chat container
  useEffect(() => {
    if (messagesEndRef.current) {
      // Use scrollIntoView with options that prevent page scrolling
      const chatContainer = messagesEndRef.current.closest('.overflow-y-auto');
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }
  }, [messages]);
  // Add effect to control body overflow when expanded
  useEffect(() => {
    if (isExpanded) {
      // Prevent scrolling on the body when expanded
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scrolling when collapsed
      document.body.style.overflow = '';
    }
    // Cleanup function to ensure we restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [isExpanded]);
  // Add handler for starting a new chat
  const handleNewChat = () => {
    // Clear messages except for the initial greeting and options
    setMessages([{
      id: 1,
      type: 'text',
      text: "Hello! I'm the Prospeum Procurement AI. I can assist you with various tasks. How can I support you?",
      sender: 'ai',
      timestamp: new Date()
    }, {
      id: 2,
      type: 'options',
      text: 'Please select an option:',
      sender: 'ai',
      timestamp: new Date(Date.now() + 100),
      data: {
        options: [{
          id: 'sourcing',
          title: 'Create and Perform a Sourcing Event',
          description: 'Start a new sourcing process to find suppliers'
        }, {
          id: 'analyze',
          title: 'Analyze a Supplier',
          description: 'Get insights and details about a specific supplier'
        }, {
          id: 'summarize',
          title: 'Summarize all important tasks',
          description: 'View a summary of your pending tasks'
        }]
      }
    }]);
    // Reset workflow state
    setCurrentWorkflow({
      stage: 'initial',
      data: {}
    });
    // Reset other states
    setNewMessage('');
    setIsAiTyping(false);
  };
  const handleSendMessage = () => {
    if (newMessage.trim() === '') return;
    const userMessage: Message = {
      id: messages.length + 1,
      type: 'text',
      text: newMessage,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages([...messages, userMessage]);
    setNewMessage('');
    setIsAiTyping(true);
    // Process user message based on current workflow stage
    if (currentWorkflow.stage === 'initial') {
      // Start the analysis workflow
      setTimeout(() => {
        const initialAnalysis: Message = {
          id: messages.length + 2,
          type: 'text',
          text: "I'm analyzing your request. Let me break this down to make sure I understand your requirements correctly.",
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, initialAnalysis]);
        setIsAiTyping(false);
        // After a short delay, ask for confirmation
        setTimeout(() => {
          const confirmationMessage: Message = {
            id: messages.length + 3,
            type: 'confirmation',
            text: `It looks like you need to source "${userMessage.text}". Is that correct?`,
            sender: 'ai',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, confirmationMessage]);
          setCurrentWorkflow({
            stage: 'analysis',
            data: {
              description: userMessage.text
            }
          });
        }, 2000);
      }, 1500);
    } else if (currentWorkflow.stage === 'analysis') {
      // Continue with clarification questions
      processAnalysisResponse(userMessage.text);
    } else if (currentWorkflow.stage === 'clarification') {
      // Process clarification responses
      processClarificationResponse(userMessage.text);
    } else if (currentWorkflow.stage === 'requirements') {
      // Process requirements input
      processRequirementsInput(userMessage.text);
    }
  };
  const processAnalysisResponse = (response: string) => {
    if (response.toLowerCase().includes('yes') || response.toLowerCase().includes('correct')) {
      // User confirmed the analysis
      setIsAiTyping(true);
      // Step 1: Acknowledge confirmation
      setTimeout(() => {
        const nextMessage: Message = {
          id: messages.length + 2,
          type: 'text',
          text: 'Great! Now I need to gather requirements for this sourcing event.',
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, nextMessage]);
        // Step 2: Introduce laptop requirements with a delay
        setTimeout(() => {
          if (currentWorkflow.data.description.toLowerCase().includes('laptop') || currentWorkflow.data.description.toLowerCase().includes('computer')) {
            const requirementsPreviewMessage: Message = {
              id: messages.length + 3,
              type: 'text',
              text: "I see you're looking to source business laptops. I have some predefined requirements for business laptops that might help you get started:",
              sender: 'ai',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, requirementsPreviewMessage]);
            // Step 3: Define the business laptop requirements
            const businessLaptopRequirements = `
1. Main Components Functionality
- CPU/Processor: Should provide efficient multitasking, support office productivity software, video conferencing, and light data analysis. A modern multi-core processor (e.g., Intel Core i5/i7 or AMD Ryzen 5/7) is preferred.
- Screen: A clear, anti-glare display with Full HD (1920×1080) resolution for comfortable daily use. Screen size between 14–15.6 inches is ideal for balancing productivity and portability.
- Audio Device: Integrated speakers and microphone suitable for video calls and online meetings, with noise-cancellation support recommended. A headphone jack and Bluetooth audio support are desirable.
- Memory Space: At least 16 GB RAM for smooth multitasking and a minimum of 512 GB SSD storage for fast boot-up, quick file access, and adequate storage of business documents and applications.
2. Size and Weight
- Slim and lightweight design to support portability for travel and commuting.
- Target weight: 1.2–1.8 kg.
- Thickness: less than 20 mm preferred.
3. Warranty
- A minimum of 3 years warranty covering parts and labor.
- On-site or next-business-day support preferred for reduced downtime.
`;
            // Step 4: Display requirements with sufficient delay (1.5s)
            setTimeout(() => {
              const requirementsDisplayMessage: Message = {
                id: messages.length + 4,
                type: 'requirements-display',
                text: businessLaptopRequirements,
                sender: 'ai',
                timestamp: new Date()
              };
              setMessages(prev => [...prev, requirementsDisplayMessage]);
              // Store the requirements in the workflow data
              setCurrentWorkflow(prev => ({
                ...prev,
                data: {
                  ...prev.data,
                  requirementsText: businessLaptopRequirements,
                  requirementsType: 'business-laptops'
                }
              }));
              // Step 5: Ask for confirmation after requirements are displayed (2s delay)
              setTimeout(() => {
                const confirmationMessage: Message = {
                  id: messages.length + 5,
                  type: 'requirements-confirmation',
                  text: 'Are these requirements acceptable, or would you like to make changes?',
                  sender: 'ai',
                  timestamp: new Date()
                };
                setMessages(prev => [...prev, confirmationMessage]);
                setIsAiTyping(false);
                // Update workflow stage to requirements with confirmation step
                setCurrentWorkflow(prev => ({
                  stage: 'requirements',
                  data: {
                    ...prev.data,
                    requirementsStep: 'confirmation'
                  }
                }));
              }, 2000); // Longer delay before asking for confirmation
            }, 1500); // Longer delay for requirements display
          } else {
            const requirementsToggleMessage: Message = {
              id: messages.length + 3,
              type: 'requirements-toggle',
              text: 'Would you like to provide your own requirements or have me generate them for you?',
              sender: 'ai',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, requirementsToggleMessage]);
            setCurrentWorkflow({
              stage: 'requirements',
              data: {
                ...currentWorkflow.data,
                requirementsStep: 'selection'
              }
            });
            setIsAiTyping(false);
          }
        }, 1500);
      }, 1000);
    } else {
      // User did not confirm, ask for clarification
      setTimeout(() => {
        const clarificationMessage: Message = {
          id: messages.length + 2,
          type: 'text',
          text: 'I apologize for misunderstanding. Could you please describe your sourcing needs again, with more details?',
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, clarificationMessage]);
        setCurrentWorkflow({
          stage: 'initial',
          data: {}
        });
        setIsAiTyping(false);
      }, 1000);
    }
  };
  const handleRequirementsSourceSelect = (source: 'ai' | 'user') => {
    // Add user message showing selected option
    const userMessage: Message = {
      id: messages.length + 1,
      type: 'text',
      text: source === 'ai' ? 'Generate requirements for me' : "I'll provide requirements",
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsAiTyping(true);
    // Update workflow data with requirements source
    setCurrentWorkflow(prev => ({
      ...prev,
      data: {
        ...prev.data,
        requirementsSource: source
      }
    }));
    setTimeout(() => {
      if (source === 'ai') {
        // AI generates requirements
        const aiGeneratingMessage: Message = {
          id: messages.length + 2,
          type: 'text',
          text: "I'll generate detailed requirements based on your sourcing needs. Here's what I recommend:",
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiGeneratingMessage]);
        // Display the generated requirements
        setTimeout(() => {
          const requirementsMessage: Message = {
            id: messages.length + 3,
            type: 'requirements-display',
            text: MOCK_REQUIREMENTS,
            sender: 'ai',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, requirementsMessage]);
          // Ask for confirmation
          setTimeout(() => {
            const confirmationMessage: Message = {
              id: messages.length + 4,
              type: 'requirements-confirmation',
              text: 'Are these requirements acceptable, or would you like to make changes?',
              sender: 'ai',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, confirmationMessage]);
            setCurrentWorkflow(prev => ({
              ...prev,
              data: {
                ...prev.data,
                requirementsStep: 'confirmation',
                requirementsText: MOCK_REQUIREMENTS
              }
            }));
            setIsAiTyping(false);
          }, 1000);
        }, 1500);
      } else {
        // User provides requirements
        const promptMessage: Message = {
          id: messages.length + 2,
          type: 'text',
          text: 'Please provide your specific requirements for this sourcing event. Include details like quantity, specifications, delivery timeline, and any other relevant information.',
          sender: 'ai',
          timestamp: new Date(),
          data: {
            requiresTextInput: true
          }
        };
        setMessages(prev => [...prev, promptMessage]);
        setCurrentWorkflow(prev => ({
          ...prev,
          data: {
            ...prev.data,
            requirementsStep: 'input'
          }
        }));
        setIsAiTyping(false);
      }
    }, 1000);
  };
  const handleRequirementsConfirmation = (confirmed: boolean) => {
    // Add user message showing response
    const userMessage: Message = {
      id: messages.length + 1,
      type: 'text',
      text: confirmed ? 'These requirements look good' : "I'd like to make changes",
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsAiTyping(true);
    if (confirmed) {
      // User confirmed the requirements
      // First, store the requirements in the workflow data and mark as confirmed
      setCurrentWorkflow(prev => ({
        ...prev,
        data: {
          ...prev.data,
          requirementsConfirmed: true
        }
      }));
      // Add a confirmation message before moving to specifications
      setTimeout(() => {
        const confirmationMessage: Message = {
          id: messages.length + 2,
          type: 'text',
          text: "Great! I've recorded these requirements for your sourcing event.",
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, confirmationMessage]);
        // Then after a short delay, ask for specifications
        setTimeout(() => {
          const specificationsMessage: Message = {
            id: messages.length + 3,
            type: 'specifications',
            text: "Now, let's get into the technical specifications. Please provide any specific technical details you need for this sourcing event.",
            sender: 'ai',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, specificationsMessage]);
          setCurrentWorkflow({
            stage: 'specifications',
            data: {
              ...currentWorkflow.data,
              requirementsConfirmed: true
            }
          });
          setIsAiTyping(false);
        }, 1500);
      }, 1000);
    } else {
      // User wants to make changes
      setTimeout(() => {
        const editMessage: Message = {
          id: messages.length + 2,
          type: 'text',
          text: "Please provide your revised requirements or specific changes you'd like to make.",
          sender: 'ai',
          timestamp: new Date(),
          data: {
            requiresTextInput: true
          }
        };
        setMessages(prev => [...prev, editMessage]);
        setCurrentWorkflow(prev => ({
          ...prev,
          data: {
            ...prev.data,
            requirementsStep: 'edit'
          }
        }));
        setIsAiTyping(false);
      }, 1000);
    }
  };
  const processRequirementsInput = (input: string) => {
    const {
      requirementsStep
    } = currentWorkflow.data;
    if (requirementsStep === 'input' || requirementsStep === 'edit') {
      // User has provided custom requirements
      setCurrentWorkflow(prev => ({
        ...prev,
        data: {
          ...prev.data,
          requirementsText: input,
          requirementsStep: 'confirmation'
        }
      }));
      setTimeout(() => {
        const confirmationMessage: Message = {
          id: messages.length + 2,
          type: 'requirements-confirmation',
          text: 'Thanks for providing your requirements. Are these requirements complete, or would you like to make additional changes?',
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, confirmationMessage]);
        setIsAiTyping(false);
      }, 1000);
    }
  };
  const processClarificationResponse = (response: string) => {
    const {
      clarificationStep
    } = currentWorkflow.data;
    if (clarificationStep === 'quantity') {
      // After quantity is set, ask about deadline
      setTimeout(() => {
        const deadlineMessage: Message = {
          id: messages.length + 2,
          type: 'calendar',
          text: 'Now, when do you need this by? Please select a target date.',
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, deadlineMessage]);
        // IMPORTANT: Use a function form to ensure we have the latest state
        setCurrentWorkflow(prev => ({
          stage: 'clarification',
          data: {
            ...prev.data,
            clarificationStep: 'deadline'
          }
        }));
        setIsAiTyping(false);
      }, 1000);
    } else if (clarificationStep === 'deadline') {
      // After deadline is set, ask about budget
      setTimeout(() => {
        const budgetQuestion: Message = {
          id: messages.length + 2,
          type: 'price-input',
          text: "What's your target price or budget for this purchase?",
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, budgetQuestion]);
        // IMPORTANT: Use a function form to ensure we have the latest state
        setCurrentWorkflow(prev => ({
          stage: 'clarification',
          data: {
            ...prev.data,
            clarificationStep: 'budget'
          }
        }));
        setIsAiTyping(false);
      }, 1000);
    } else if (clarificationStep === 'budget') {
      // Complete the clarification process
      setTimeout(() => {
        const completionMessage: Message = {
          id: messages.length + 2,
          type: 'text',
          text: "Thank you for providing all the details. I've analyzed your requirements and found several potential suppliers that match your needs.",
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, completionMessage]);
        // Add supplier selection message
        setTimeout(() => {
          // Create a new sourcing event if it doesn't exist yet
          if (!sourcingEvent) {
            createNewEvent(`Sourcing for ${currentWorkflow.data.description}`, currentWorkflow.data.description, currentWorkflow.data.deadline, currentWorkflow.data.budget, currentWorkflow.data.requirementsText);
            // Add mock suppliers to the event
            addSuppliers(MOCK_SUPPLIERS);
          }
          // Show supplier selection in chat
          const supplierSelectionMessage: Message = {
            id: messages.length + 3,
            type: 'supplier-selection',
            text: "Please select at least 3 suppliers that you'd like to include in your sourcing event:",
            sender: 'ai',
            timestamp: new Date(),
            data: {
              suppliers: MOCK_SUPPLIERS,
              minRequired: 3
            }
          };
          setMessages(prev => [...prev, supplierSelectionMessage]);
          // Update workflow stage
          setCurrentWorkflow(prev => ({
            stage: 'supplier_selection',
            data: {
              ...prev.data
            }
          }));
          setIsAiTyping(false);
        }, 1000);
      }, 1000);
    }
  };
  const handleCalendarSelect = (date: Date) => {
    // Add user message showing selected date
    const userResponse: Message = {
      id: messages.length + 1,
      type: 'text',
      text: `I need this by ${date.toLocaleDateString()}.`,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userResponse]);
    setIsAiTyping(true);
    console.log('CALENDAR: Selected date object:', date);
    // CRITICAL FIX: Store the date as a Date object
    setCurrentWorkflow(prevWorkflow => ({
      ...prevWorkflow,
      data: {
        ...prevWorkflow.data,
        deadline: date // Ensure this is a Date object
      }
    }));
    // Process the response after state update
    setTimeout(() => {
      processClarificationResponse(userResponse.text);
    }, 100);
  };
  const handlePriceInput = (price: number) => {
    // Add user message showing input price
    const userResponse: Message = {
      id: messages.length + 1,
      type: 'text',
      text: `My budget is €${price.toLocaleString()}.`,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userResponse]);
    setIsAiTyping(true);
    // Log the budget value for debugging
    console.log('BUDGET: Entered budget value:', price, 'Type:', typeof price);
    // CRITICAL FIX: Store the budget as a number in workflow data
    setCurrentWorkflow(prevWorkflow => {
      console.log('Updating currentWorkflow budget from', prevWorkflow.data.budget, 'to', price);
      return {
        ...prevWorkflow,
        data: {
          ...prevWorkflow.data,
          budget: Number(price) // Ensure this is a number
        }
      };
    });
    // If sourcingEvent already exists, update it directly with budget
    if (sourcingEvent) {
      const updatedEvent = {
        ...sourcingEvent,
        requirements: {
          ...sourcingEvent.requirements,
          budget: Number(price)
        }
      };
      setSourcingEvent(updatedEvent);
      console.log('Updated sourcingEvent budget directly:', {
        budget: updatedEvent.requirements.budget,
        budgetType: typeof updatedEvent.requirements.budget
      });
    }
    // Process the response after state update
    setTimeout(() => {
      processClarificationResponse(userResponse.text);
    }, 100);
  };
  // Improved handler for supplier selection
  const handleSupplierSelection = supplierId => {
    console.log(`Selection triggered for supplier: ${supplierId}`);
    // Directly update the context state without setTimeout
    toggleSupplierSelection(supplierId);
    // No need to maintain a separate local state - we'll derive it from context
    // This ensures UI and data are always in sync
  };
  // Improved function to continue to review after selecting suppliers
  const handleContinueToReview = () => {
    if (sourcingEvent) {
      console.log('Current selection state before review:', {
        suppliers: sourcingEvent.suppliers,
        selectedSuppliers: sourcingEvent.suppliers.filter(s => s.selected)
      });
      // Get the actually selected suppliers for the summary
      const selectedSuppliersList = sourcingEvent.suppliers.filter(s => s.selected);
      const selectedSupplierNames = selectedSuppliersList.map(s => s.name);
      console.log('Selected suppliers for summary:', {
        count: selectedSuppliersList.length,
        names: selectedSupplierNames
      });
      // Make sure to update the sourcingEvent with specifications and budget before changing status
      // CRITICAL FIX: Ensure specifications is properly set from the currentWorkflow
      const specifications = currentWorkflow.data.specifications || currentWorkflow.data.requirementsText || '';
      const requirementsText = currentWorkflow.data.requirementsText || '';
      const budget = currentWorkflow.data.budget || sourcingEvent.requirements.budget;
      console.log('Budget value before update:', {
        workflowBudget: currentWorkflow.data.budget,
        sourcingEventBudget: sourcingEvent.requirements.budget,
        finalBudget: budget,
        budgetType: typeof budget
      });
      // CRITICAL FIX: ALWAYS use specifications as requirementsText
      const updatedEvent = {
        ...sourcingEvent,
        requirements: {
          ...sourcingEvent.requirements,
          specifications: specifications,
          requirementsText: requirementsText || specifications,
          budget: budget,
          quantity: currentWorkflow.data.quantity || sourcingEvent.requirements.quantity // Include quantity
        }
      };
      // Update the event in context
      setSourcingEvent(updatedEvent);
      // Debug log to verify data
      console.log('Updated sourcingEvent before review:', {
        specifications: updatedEvent.requirements.specifications,
        requirementsText: updatedEvent.requirements.requirementsText,
        budget: updatedEvent.requirements.budget,
        budgetType: typeof updatedEvent.requirements.budget
      });
      // Update the event status
      updateEventStatus('review');
      // Add a summary artifact message to the chat
      const summaryMessage = {
        id: messages.length + 1,
        type: 'sourcing-summary',
        text: "I've created your sourcing event. Here's a summary:",
        sender: 'ai',
        timestamp: new Date(),
        data: {
          event: {
            title: updatedEvent.requirements.title,
            description: updatedEvent.requirements.description,
            deadline: updatedEvent.requirements.deadline,
            budget: updatedEvent.requirements.budget,
            quantity: updatedEvent.requirements.quantity,
            selectedSuppliers: selectedSupplierNames,
            // CRITICAL FIX: Ensure specifications is correctly passed to the message data
            specifications: specifications,
            requirementsText: updatedEvent.requirements.requirementsText,
            requirementsPreview: specifications ? specifications.substring(0, 150) + '...' : 'No detailed requirements provided'
          }
        }
      };
      setMessages(prev => [...prev, summaryMessage]);
    }
  };
  // Function to navigate to review page from the artifact
  const handleNavigateToReview = () => {
    // Double-check that our context has the updated suppliers before navigating
    if (sourcingEvent) {
      const selectedSuppliersList = sourcingEvent.suppliers.filter(s => s.selected);
      console.log('Navigating to review with selected suppliers:', {
        count: selectedSuppliersList.length,
        names: selectedSuppliersList.map(s => s.name)
      });
      // Ensure specifications and budget are saved in the context before navigating
      // CRITICAL FIX: Ensure we're getting specifications from the right place
      const specifications = currentWorkflow.data.specifications || currentWorkflow.data.requirementsText || '';
      const requirementsText = currentWorkflow.data.requirementsText || '';
      const budget = currentWorkflow.data.budget !== undefined ? currentWorkflow.data.budget : sourcingEvent.requirements.budget;
      // Get the quantity from workflow data or existing sourcingEvent
      const quantity = currentWorkflow.data.quantity !== undefined ? currentWorkflow.data.quantity : sourcingEvent.requirements.quantity;
      console.log('Budget and quantity values before navigation:', {
        workflowBudget: currentWorkflow.data.budget,
        sourcingEventBudget: sourcingEvent.requirements.budget,
        finalBudget: budget,
        budgetType: typeof budget,
        workflowQuantity: currentWorkflow.data.quantity,
        sourcingEventQuantity: sourcingEvent.requirements.quantity,
        finalQuantity: quantity,
        quantityType: typeof quantity
      });
      // CRITICAL FIX: ALWAYS use specifications as requirementsText and include quantity
      const updatedEvent = {
        ...sourcingEvent,
        requirements: {
          ...sourcingEvent.requirements,
          specifications: specifications,
          requirementsText: requirementsText || specifications,
          budget: budget,
          quantity: quantity // CRITICAL FIX: Include quantity in the updated event
        }
      };
      setSourcingEvent(updatedEvent);
      // Debug log to verify data before navigation
      console.log('Final sourcingEvent before navigation:', {
        specifications: updatedEvent.requirements.specifications,
        requirementsText: updatedEvent.requirements.requirementsText,
        budget: updatedEvent.requirements.budget,
        budgetType: typeof updatedEvent.requirements.budget,
        quantity: updatedEvent.requirements.quantity,
        quantityType: typeof updatedEvent.requirements.quantity
      });
    }
    navigate('/sourcing/review');
  };
  // Render supplier selection component
  const renderSupplierSelection = message => {
    // If sourcingEvent exists, get the currently selected supplier IDs from context
    const selectedSupplierIds = sourcingEvent?.suppliers.filter(s => s.selected).map(s => s.id) || [];
    console.log('Current selected supplier IDs from context:', selectedSupplierIds);
    return <div className="w-full" data-id="element-728">
        <p className="mb-3" data-id="element-729">{message.text}</p>
        <div className="space-y-3" data-id="element-730">
          {message.data.suppliers.map(supplier => {
          // First ensure this supplier exists in the context
          const supplierInContext = sourcingEvent?.suppliers.find(s => s.id === supplier.id);
          // Check if supplier is selected in the context
          const isSelected = supplierInContext?.selected || false;
          console.log(`Rendering supplier ${supplier.name}:`, {
            id: supplier.id,
            contextSelected: isSelected,
            supplierExists: !!supplierInContext
          });
          return <SupplierCard key={supplier.id} supplier={{
            ...supplier,
            selected: isSelected
          }} // Pass the selection state directly
          selected={isSelected} onSelect={() => {
            console.log(`Selection triggered for supplier: ${supplier.id}`);
            toggleSupplierSelection(supplier.id);
          }} data-id="element-731" />;
        })}
        </div>
        <div className="mt-4 flex justify-between items-center" data-id="element-732">
          <div className="text-sm text-gray-600" data-id="element-733">
            {selectedSupplierIds.length} of {message.data.minRequired} suppliers
            selected
          </div>
          {selectedSupplierIds.length >= message.data.minRequired && <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center" onClick={handleContinueToReview} data-id="element-734">
              Continue to Review
              <ChevronRight size={16} className="ml-1" data-id="element-735" />
            </button>}
        </div>
      </div>;
  };
  // Make sure the suppliers are properly added to the context when the component mounts
  useEffect(() => {
    // If we have suppliers in the message but they're not in the context, add them
    const supplierSelectionMessage = messages.find(m => m.type === 'supplier-selection');
    if (supplierSelectionMessage && sourcingEvent && (!sourcingEvent.suppliers || sourcingEvent.suppliers.length === 0)) {
      console.log('Adding suppliers to context from message:', supplierSelectionMessage.data.suppliers.map(s => s.name));
      addSuppliers(supplierSelectionMessage.data.suppliers);
    }
  }, [messages, sourcingEvent, addSuppliers]);
  // Add handler for option selection
  const handleOptionSelect = (optionId: string) => {
    // Add user message showing selected option
    const selectedOption = messages.find(m => m.type === 'options')?.data?.options.find(opt => opt.id === optionId);
    if (!selectedOption) return;
    const userMessage: Message = {
      id: messages.length + 1,
      type: 'text',
      text: selectedOption.title,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages([...messages, userMessage]);
    setIsAiTyping(true);
    // Process based on which option was selected
    if (optionId === 'sourcing') {
      // Start the sourcing workflow
      setTimeout(() => {
        const sourcingMessage: Message = {
          id: messages.length + 2,
          type: 'text',
          text: "I'll help you create a new sourcing event. Please describe what you're looking to source or upload a requirements document.",
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, sourcingMessage]);
        setIsAiTyping(false);
        setCurrentWorkflow({
          stage: 'initial',
          data: {}
        });
      }, 1000);
    } else if (optionId === 'analyze') {
      // Start the supplier analysis workflow
      setTimeout(() => {
        const analyzeMessage: Message = {
          id: messages.length + 2,
          type: 'text',
          text: "I can help analyze a supplier for you. Please provide the name of the supplier you'd like to analyze.",
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, analyzeMessage]);
        setIsAiTyping(false);
        setCurrentWorkflow({
          stage: 'supplier_analysis',
          data: {
            step: 'supplier_name'
          }
        });
      }, 1000);
    } else if (optionId === 'summarize') {
      // Provide a task summary
      setTimeout(() => {
        const summaryMessage: Message = {
          id: messages.length + 2,
          type: 'text',
          text: "Here's a summary of your important tasks:\n\n• 3 pending supplier reviews\n• 2 sourcing events awaiting your approval\n• 1 contract renewal due in 15 days\n• 5 invoices pending payment verification",
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, summaryMessage]);
        setIsAiTyping(false);
      }, 1000);
    }
  };
  // Add handler to continue to suppliers
  const handleContinueToSuppliers = () => {
    // IMPORTANT: Get the latest state directly from the currentWorkflow
    console.log('FINAL STATE BEFORE NAVIGATION:', {
      currentWorkflow: currentWorkflow,
      sourcingEvent: sourcingEvent
    });
    // Debug the actual values we're going to use
    console.log('CRITICAL VALUES CHECK:', {
      deadline: currentWorkflow.data.deadline,
      deadlineType: typeof currentWorkflow.data.deadline,
      budget: currentWorkflow.data.budget,
      budgetType: typeof currentWorkflow.data.budget,
      description: currentWorkflow.data.description,
      requirementsText: currentWorkflow.data.requirementsText,
      requirementsSource: currentWorkflow.data.requirementsSource,
      specifications: currentWorkflow.data.specifications
    });
    // Safely extract values, ensuring they're the correct types
    const deadline = currentWorkflow.data.deadline;
    let budget = currentWorkflow.data.budget;
    // Ensure budget is a number
    if (typeof budget === 'string') {
      budget = parseFloat(budget);
      if (isNaN(budget)) budget = undefined;
    }
    const specifications = currentWorkflow.data.specifications || '';
    // CRITICAL FIX: ALWAYS use specifications as requirementsText
    const finalRequirementsText = specifications;
    // Create a new event if one doesn't exist yet
    if (!sourcingEvent) {
      createNewEvent(`Sourcing for ${currentWorkflow.data.description}`, currentWorkflow.data.description, deadline, budget, finalRequirementsText, specifications, currentWorkflow.data.quantity);
    } else {
      // If the event already exists, update it with the latest values
      // Make a deep copy to ensure we're not directly mutating state
      const updatedEvent = {
        ...sourcingEvent,
        requirements: {
          ...sourcingEvent.requirements,
          deadline: deadline,
          budget: budget,
          requirementsText: finalRequirementsText,
          requirementsSource: currentWorkflow.data.requirementsSource,
          specifications: specifications
        }
      };
      // Use the context's setter to update the event
      setSourcingEvent(updatedEvent);
      // Debug log to verify data
      console.log('Updated sourcingEvent before supplier selection:', {
        specifications: updatedEvent.requirements.specifications,
        requirementsText: updatedEvent.requirements.requirementsText,
        budget: updatedEvent.requirements.budget,
        budgetType: typeof updatedEvent.requirements.budget
      });
    }
    // Update the sourcing event status and navigate to supplier selection
    updateEventStatus('supplier_selection');
    navigate('/sourcing/selection');
  };
  // Add handler for keyboard events
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  // Add handler for file upload
  const handleFileUpload = () => {
    // This would be implemented to handle file uploads
    alert('File upload functionality would be implemented here');
  };
  // Add new handler for specifications input
  const handleSpecificationsSubmit = (specifications: string) => {
    // Add user message showing specifications
    const userMessage: Message = {
      id: messages.length + 1,
      type: 'text',
      text: specifications ? `I'll use these specifications: ${specifications.split('\n')[0]}...` : 'No additional specifications needed',
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsAiTyping(true);
    // Store specifications in workflow data
    setCurrentWorkflow(prev => ({
      ...prev,
      data: {
        ...prev.data,
        specifications: specifications
      }
    }));
    // If sourcingEvent already exists, update it directly with specifications
    // CRITICAL FIX: Always set specifications as requirementsText to ensure they're carried over
    if (sourcingEvent) {
      const updatedEvent = {
        ...sourcingEvent,
        requirements: {
          ...sourcingEvent.requirements,
          specifications: specifications,
          // CRITICAL FIX: Always use specifications as requirementsText
          requirementsText: specifications
        }
      };
      setSourcingEvent(updatedEvent);
      console.log('Updated sourcingEvent with specifications:', {
        specifications: updatedEvent.requirements.specifications,
        requirementsText: updatedEvent.requirements.requirementsText
      });
    }
    // Acknowledge specifications and move to quantity instead of deadline
    setTimeout(() => {
      const acknowledgmentMessage: Message = {
        id: messages.length + 2,
        type: 'text',
        text: "Thank you for confirming the specifications. I've recorded them for your sourcing event.",
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, acknowledgmentMessage]);
      // Then after a short delay, ask for quantity
      setTimeout(() => {
        const quantityMessage: Message = {
          id: messages.length + 3,
          type: 'quantity-input',
          text: 'How many units do you need? Please specify the quantity.',
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, quantityMessage]);
        setCurrentWorkflow({
          stage: 'clarification',
          data: {
            ...currentWorkflow.data,
            clarificationStep: 'quantity',
            specificationsConfirmed: true
          }
        });
        setIsAiTyping(false);
      }, 1500);
    }, 1000);
  };
  // Add handler for quantity input
  const handleQuantityInput = (quantity: number) => {
    // Add user message showing input quantity
    const userResponse: Message = {
      id: messages.length + 1,
      type: 'text',
      text: `I need ${quantity.toLocaleString()} units.`,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userResponse]);
    setIsAiTyping(true);
    // Log the quantity value for debugging
    console.log('QUANTITY: Entered quantity value:', quantity, 'Type:', typeof quantity);
    // Store the quantity as a number in workflow data
    setCurrentWorkflow(prevWorkflow => {
      console.log('Updating currentWorkflow quantity from', prevWorkflow.data.quantity, 'to', quantity);
      return {
        ...prevWorkflow,
        data: {
          ...prevWorkflow.data,
          quantity: Number(quantity) // Ensure this is a number
        }
      };
    });
    // If sourcingEvent already exists, update it directly with quantity
    if (sourcingEvent) {
      const updatedEvent = {
        ...sourcingEvent,
        requirements: {
          ...sourcingEvent.requirements,
          quantity: Number(quantity)
        }
      };
      setSourcingEvent(updatedEvent);
      console.log('Updated sourcingEvent quantity directly:', {
        quantity: updatedEvent.requirements.quantity,
        quantityType: typeof updatedEvent.requirements.quantity
      });
    }
    // Process the response after state update
    setTimeout(() => {
      processClarificationResponse(userResponse.text);
    }, 100);
  };
  const renderMessageContent = (message: Message) => {
    switch (message.type) {
      case 'text':
        return <div className="whitespace-pre-wrap" data-id="element-736">{message.text}</div>;
      case 'options':
        return <div className="w-full" data-id="element-737">
            <p className="mb-4" data-id="element-738">{message.text}</p>
            <div className="grid gap-3" data-id="element-739">
              {message.data?.options?.map(option => <button key={option.id} className="p-4 text-left border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors" onClick={() => handleOptionSelect(option.id)} data-id="element-740">
                  <div className="font-medium text-gray-900" data-id="element-741">
                    {option.title}
                  </div>
                  <div className="text-sm text-gray-600 mt-1" data-id="element-742">
                    {option.description}
                  </div>
                </button>)}
            </div>
          </div>;
      case 'confirmation':
        return <div className="w-full" data-id="element-743">
            <p className="mb-4" data-id="element-744">{message.text}</p>
            <div className="flex gap-3" data-id="element-745">
              <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors" onClick={() => handleRequirementsConfirmation(true)} data-id="element-746">
                Yes, that's correct
              </button>
              <button className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors" onClick={() => handleRequirementsConfirmation(false)} data-id="element-747">
                No, let me clarify
              </button>
            </div>
          </div>;
      case 'calendar':
        return <div className="w-full" data-id="element-748">
            <p className="mb-4" data-id="element-749">{message.text}</p>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center" onClick={() => setShowCalendarModal(true)} data-id="element-750">
              <CalendarIcon size={16} className="mr-2" data-id="element-751" />
              Select Date
            </button>
          </div>;
      case 'price-input':
        return <div className="w-full" data-id="element-752">
            <p className="mb-4" data-id="element-753">{message.text}</p>
            <div className="flex items-center gap-3" data-id="element-754">
              <div className="relative" data-id="element-755">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-756">
                  €
                </div>
                <input type="number" placeholder="Enter amount" className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" onKeyPress={e => {
                if (e.key === 'Enter') {
                  const value = parseFloat((e.target as HTMLInputElement).value);
                  if (value > 0) {
                    handlePriceInput(value);
                  }
                }
              }} data-id="element-757" />
              </div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" onClick={e => {
              const input = (e.target as HTMLButtonElement).previousElementSibling?.querySelector('input') as HTMLInputElement;
              const value = parseFloat(input?.value || '0');
              if (value > 0) {
                handlePriceInput(value);
              }
            }} data-id="element-758">
                Submit
              </button>
            </div>
          </div>;
      case 'requirements-toggle':
        return <div className="w-full" data-id="element-759">
            <p className="mb-4" data-id="element-760">{message.text}</p>
            <div className="grid gap-3" data-id="element-761">
              <button className="p-4 text-left border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors" onClick={() => handleRequirementsSourceSelect('ai')} data-id="element-762">
                <div className="font-medium text-gray-900" data-id="element-763">
                  Generate requirements for me
                </div>
                <div className="text-sm text-gray-600 mt-1" data-id="element-764">
                  I'll create detailed requirements based on your needs
                </div>
              </button>
              <button className="p-4 text-left border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors" onClick={() => handleRequirementsSourceSelect('user')} data-id="element-765">
                <div className="font-medium text-gray-900" data-id="element-766">
                  I'll provide my own requirements
                </div>
                <div className="text-sm text-gray-600 mt-1" data-id="element-767">
                  You can input your specific requirements
                </div>
              </button>
            </div>
          </div>;
      case 'requirements-display':
        return <div className="w-full bg-white border-2 border-blue-200 rounded-lg p-4 my-4" data-id="element-768">
            <h3 className="font-medium text-lg text-gray-800 mb-3 border-b pb-2" data-id="element-769">
              Business Laptop Requirements
            </h3>
            <div className="text-sm text-gray-800" data-id="element-770">
              <div className="space-y-4" data-id="element-771">
                <section data-id="element-772">
                  <h4 className="font-medium text-gray-800 mb-2" data-id="element-773">
                    1. Main Components Functionality
                  </h4>
                  <div className="space-y-2 ml-2" data-id="element-774">
                    <p data-id="element-775">
                      <span className="font-medium" data-id="element-776">CPU/Processor:</span> Should
                      provide efficient multitasking, support office
                      productivity software, video conferencing, and light data
                      analysis. A modern multi-core processor (e.g., Intel Core
                      i5/i7 or AMD Ryzen 5/7) is preferred.
                    </p>
                    <p data-id="element-777">
                      <span className="font-medium" data-id="element-778">Screen:</span> A clear,
                      anti-glare display with Full HD (1920×1080) resolution for
                      comfortable daily use. Screen size between 14–15.6 inches
                      is ideal for balancing productivity and portability.
                    </p>
                    <p data-id="element-779">
                      <span className="font-medium" data-id="element-780">Audio Device:</span>{' '}
                      Integrated speakers and microphone suitable for video
                      calls and online meetings, with noise-cancellation support
                      recommended. A headphone jack and Bluetooth audio support
                      are desirable.
                    </p>
                    <p data-id="element-781">
                      <span className="font-medium" data-id="element-782">Memory Space:</span> At
                      least 16 GB RAM for smooth multitasking and a minimum of
                      512 GB SSD storage for fast boot-up, quick file access,
                      and adequate storage of business documents and
                      applications.
                    </p>
                  </div>
                </section>
                <section data-id="element-783">
                  <h4 className="font-medium text-gray-800 mb-2" data-id="element-784">
                    2. Size and Weight
                  </h4>
                  <div className="space-y-2 ml-2" data-id="element-785">
                    <p data-id="element-786">
                      Slim and lightweight design to support portability for
                      travel and commuting.
                    </p>
                    <p data-id="element-787">Target weight: 1.2–1.8 kg.</p>
                    <p data-id="element-788">Thickness: less than 20 mm preferred.</p>
                  </div>
                </section>
                <section data-id="element-789">
                  <h4 className="font-medium text-gray-800 mb-2" data-id="element-790">
                    3. Warranty
                  </h4>
                  <div className="space-y-2 ml-2" data-id="element-791">
                    <p data-id="element-792">
                      A minimum of 3 years warranty covering parts and labor.
                    </p>
                    <p data-id="element-793">
                      On-site or next-business-day support preferred for reduced
                      downtime.
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </div>;
      case 'requirements-confirmation':
        return <div className="w-full" data-id="element-794">
            <p className="mb-4" data-id="element-795">{message.text}</p>
            <div className="flex gap-3" data-id="element-796">
              <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors" onClick={() => handleRequirementsConfirmation(true)} data-id="element-797">
                Accept Requirements
              </button>
              <button className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors" onClick={() => handleRequirementsConfirmation(false)} data-id="element-798">
                Make Changes
              </button>
            </div>
          </div>;
      case 'specifications':
        return <div className="w-full" data-id="element-799">
            <p className="mb-4" data-id="element-800">{message.text}</p>
            <div className="relative" data-id="element-801">
              <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 min-h-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter technical specifications or modify the suggested ones." rows={8} id="specifications-input" defaultValue={`Business Laptop Specifications
Main Components Functionality
CPU/Processor: Should provide efficient multitasking, support office productivity software, video conferencing, and light data analysis. A modern multi-core processor (e.g., Intel Core i5/i7 or AMD Ryzen 5/7) is preferred.
Screen: A clear, anti-glare display with Full HD (1920×1080) resolution for comfortable daily use. Screen size between 14–15.6 inches is ideal for balancing productivity and portability.
Audio Device: Integrated speakers and microphone suitable for video calls and online meetings, with noise-cancellation support recommended. A headphone jack and Bluetooth audio support are desirable.
Memory Space (RAM & Storage): At least 16 GB RAM for smooth multitasking and a minimum of 512 GB SSD storage for fast boot-up, quick file access, and adequate storage of business documents and applications.
Size and Weight
Slim and lightweight design to support portability for travel and commuting.
Target weight: 1.2–1.8 kg.
Thickness: less than 20 mm preferred.
Warranty
A minimum of 3 years warranty covering parts and labor.
On-site or next-business-day support preferred for reduced downtime.`} data-id="element-802"></textarea>
              <div className="mt-3 flex justify-between" data-id="element-803">
                <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center" onClick={() => {
                // Accept the default specifications as is
                const textarea = document.getElementById('specifications-input') as HTMLTextAreaElement;
                handleSpecificationsSubmit(textarea?.value || '');
              }} data-id="element-804">
                  <CheckCircleIcon size={16} className="mr-2" data-id="element-805" />
                  Accept Specifications
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" onClick={() => {
                const textarea = document.getElementById('specifications-input') as HTMLTextAreaElement;
                handleSpecificationsSubmit(textarea?.value || '');
              }} data-id="element-806">
                  Submit Modified Specifications
                </button>
              </div>
            </div>
          </div>;
      case 'quantity-input':
        return <div className="w-full" data-id="element-807">
            <p className="mb-4" data-id="element-808">{message.text}</p>
            <div className="flex items-center gap-3" data-id="element-809">
              <input type="number" placeholder="Enter quantity" min="1" className="pl-3 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" onKeyPress={e => {
              if (e.key === 'Enter') {
                const value = parseInt((e.target as HTMLInputElement).value);
                if (value > 0) {
                  handleQuantityInput(value);
                }
              }
            }} data-id="element-810" />
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" onClick={e => {
              const input = (e.target as HTMLButtonElement).previousElementSibling as HTMLInputElement;
              const value = parseInt(input?.value || '0');
              if (value > 0) {
                handleQuantityInput(value);
              }
            }} data-id="element-811">
                Submit
              </button>
            </div>
          </div>;
      case 'supplier-selection':
        return renderSupplierSelection(message);
      case 'sourcing-summary':
        return <div className="w-full" data-id="element-812">
            <p className="mb-4" data-id="element-813">{message.text}</p>
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm" data-id="element-814">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-815">
                Sourcing Event Summary
              </h3>
              <div className="space-y-3" data-id="element-816">
                <div data-id="element-817">
                  <span className="font-medium text-gray-700" data-id="element-818">Title:</span>
                  <span className="ml-2 text-gray-900" data-id="element-819">
                    {message.data?.event?.title}
                  </span>
                </div>
                <div data-id="element-820">
                  <span className="font-medium text-gray-700" data-id="element-821">
                    Description:
                  </span>
                  <span className="ml-2 text-gray-900" data-id="element-822">
                    {message.data?.event?.description}
                  </span>
                </div>
                <div data-id="element-823">
                  <span className="font-medium text-gray-700" data-id="element-824">Quantity:</span>
                  <span className="ml-2 text-gray-900" data-id="element-825">
                    {message.data?.event?.quantity ? message.data.event.quantity.toLocaleString() : 'Not specified'}{' '}
                    units
                  </span>
                </div>
                <div data-id="element-826">
                  <span className="font-medium text-gray-700" data-id="element-827">Deadline:</span>
                  <span className="ml-2 text-gray-900" data-id="element-828">
                    {message.data?.event?.deadline ? new Date(message.data.event.deadline).toLocaleDateString() : 'Not specified'}
                  </span>
                </div>
                <div data-id="element-829">
                  <span className="font-medium text-gray-700" data-id="element-830">Budget:</span>
                  <span className="ml-2 text-gray-900" data-id="element-831">
                    {message.data?.event?.budget ? `€${message.data.event.budget.toLocaleString()}` : 'Not specified'}
                  </span>
                </div>
                <div data-id="element-832">
                  <span className="font-medium text-gray-700" data-id="element-833">
                    Selected Suppliers:
                  </span>
                  <span className="ml-2 text-gray-900" data-id="element-834">
                    {message.data?.event?.selectedSuppliers?.join(', ') || 'None selected'}
                  </span>
                </div>
                <div data-id="element-835">
                  <span className="font-medium text-gray-700" data-id="element-836">
                    Detailed Requirements:
                  </span>
                  <div className="ml-2 text-gray-600 text-sm mt-1 bg-gray-50 p-3 rounded-md max-h-40 overflow-y-auto whitespace-pre-line" data-id="element-837">
                    {message.data?.event?.specifications || 'No detailed requirements provided'}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3" data-id="element-838">
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" onClick={handleNavigateToReview} data-id="element-839">
                  View Full Details
                </button>
                <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors" onClick={handleContinueToSuppliers} data-id="element-840">
                  Go to Supplier Selection
                </button>
              </div>
            </div>
          </div>;
      default:
        return <div className="whitespace-pre-wrap" data-id="element-841">{message.text}</div>;
    }
  };
  // Add state for requirements modal
  const [showRequirementsModal, setShowRequirementsModal] = useState(false);
  // Remove or comment out the duplicate handleRequirementsResponse function
  // as it's causing confusion in the flow
  /*
  const handleRequirementsResponse = (confirmed: boolean) => {
    // This function is redundant and is causing the issue
    // Its functionality has been merged into handleRequirementsConfirmation
  }
  */
  return <div className={`${isExpanded ? 'fixed inset-0 z-[9999] w-screen h-screen bg-gray-50 overflow-hidden' : 'bg-white rounded-lg shadow-md overflow-hidden h-[500px]'} flex transition-all duration-300`} style={{
    maxWidth: isExpanded ? '100vw' : 'none',
    maxHeight: isExpanded ? '100vh' : 'none'
  }} data-id="element-842">
      {/* Left Sidebar - Only visible when expanded or sidebar is open */}
      <div className={`${isExpanded || isSidebarOpen ? 'w-64 border-r border-gray-200' : 'w-0'} bg-white transition-all duration-300 flex flex-col`} data-id="element-843">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200 flex items-center" data-id="element-844">
          <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold" data-id="element-845">
            P
          </div>
          <h2 className="ml-2 font-semibold text-gray-800" data-id="element-846">Prospeum AI</h2>
        </div>
        {/* New Chat Button */}
        <div className="p-3" data-id="element-847">
          <button className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors" onClick={handleNewChat} data-id="element-848">
            <PlusIcon size={16} data-id="element-849" />
            <span data-id="element-850">New chat</span>
          </button>
        </div>
        {/* Recent Chats */}
        <div className="flex-1 overflow-y-auto p-2" data-id="element-851">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 mb-2" data-id="element-852">
            Recent chats
          </h3>
          <div className="space-y-1" data-id="element-853">
            {/* These would be populated from actual chat history */}
            <div className="px-3 py-2 hover:bg-gray-100 rounded cursor-pointer text-sm truncate text-gray-700 flex items-center" data-id="element-854">
              <span className="w-4 h-4 mr-2 text-gray-400" data-id="element-855">
                <MenuIcon size={16} data-id="element-856" />
              </span>
              MacBook Pro sourcing
            </div>
            <div className="px-3 py-2 hover:bg-gray-100 rounded cursor-pointer text-sm truncate text-gray-700 flex items-center" data-id="element-857">
              <span className="w-4 h-4 mr-2 text-gray-400" data-id="element-858">
                <MenuIcon size={16} data-id="element-859" />
              </span>
              Office supplies vendor analysis
            </div>
            <div className="px-3 py-2 hover:bg-gray-100 rounded cursor-pointer text-sm truncate text-gray-700 flex items-center" data-id="element-860">
              <span className="w-4 h-4 mr-2 text-gray-400" data-id="element-861">
                <MenuIcon size={16} data-id="element-862" />
              </span>
              Cloud services procurement
            </div>
          </div>
        </div>
      </div>
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full" data-id="element-863">
        {/* Chat Header */}
        <div className="border-b border-gray-200 p-3 flex justify-between items-center bg-white" data-id="element-864">
          {!isExpanded && !isSidebarOpen && <button className="p-1 rounded-full text-gray-500 hover:bg-gray-100" onClick={() => setIsSidebarOpen(true)} data-id="element-865">
              <MenuIcon size={20} data-id="element-866" />
            </button>}
          <h2 className={`font-medium text-gray-800 ${!isExpanded && !isSidebarOpen ? 'ml-2' : ''}`} data-id="element-867">
            {sourcingEvent?.requirements?.title || 'Procurement Assistant'}
          </h2>
          <div className="flex items-center gap-2" data-id="element-868">
            {isExpanded && <button className="p-1 rounded-full text-gray-500 hover:bg-gray-100" onClick={handleNewChat} data-id="element-869">
                <PlusIcon size={20} data-id="element-870" />
              </button>}
            <button className="p-1 rounded-full text-gray-500 hover:bg-gray-100" onClick={toggleExpandedMode} aria-label={isExpanded ? 'Minimize' : 'Expand'} data-id="element-871">
              {isExpanded ? <MinimizeIcon size={20} data-id="element-872" /> : <MaximizeIcon size={20} data-id="element-873" />}
            </button>
          </div>
        </div>
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50" style={{
        height: isExpanded ? 'calc(100vh - 56px - 64px)' : 'calc(500px - 56px - 64px)'
      }} data-id="element-874">
          <div className="max-w-[60%] mx-auto" data-id="element-875">
            {messages.map(message => <div key={message.id} className="mb-6" data-id="element-876">
                {message.sender === 'user' ?
            // User message - keep as chat bubble
            <div className="flex justify-end" data-id="element-877">
                    <div className="max-w-3xl ml-auto" data-id="element-878">
                      <div className="flex items-start" data-id="element-879">
                        <div className="flex-1 text-right" data-id="element-880">
                          <div className="inline-block text-left px-4 py-3 rounded-lg bg-blue-600 text-white" data-id="element-881">
                            {renderMessageContent(message)}
                          </div>
                        </div>
                        <div className="h-8 w-8 rounded-full bg-gray-300 flex-shrink-0 flex items-center justify-center text-gray-700 font-semibold ml-3 mt-1" data-id="element-882">
                          U
                        </div>
                      </div>
                    </div>
                  </div> :
            // AI message - full canvas style without avatar
            <div className="w-full" data-id="element-883">
                    <div className="text-gray-800 leading-relaxed" data-id="element-884">
                      {renderMessageContent(message)}
                    </div>
                  </div>}
              </div>)}
            {isAiTyping && <div className="mb-6" data-id="element-885">
                <div className="flex space-x-1" data-id="element-886">
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-887"></div>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-888"></div>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-889"></div>
                </div>
              </div>}
            {showCalendarModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-890">
                <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md" data-id="element-891">
                  <div className="flex justify-between items-center mb-4" data-id="element-892">
                    <h3 className="text-lg font-semibold" data-id="element-893">Select a Date</h3>
                    <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowCalendarModal(false)} data-id="element-894">
                      <XIcon size={20} data-id="element-895" />
                    </button>
                  </div>
                  <div className="mb-4" data-id="element-896">
                    <div className="flex justify-between items-center mb-2" data-id="element-897">
                      <button className="p-1 rounded-full hover:bg-gray-100" onClick={prevMonth} data-id="element-898">
                        <ChevronLeftIcon size={20} data-id="element-899" />
                      </button>
                      <div className="font-medium" data-id="element-900">
                        {currentMonth.toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric'
                    })}
                      </div>
                      <button className="p-1 rounded-full hover:bg-gray-100" onClick={nextMonth} data-id="element-901">
                        <ChevronRightIcon size={20} data-id="element-902" />
                      </button>
                    </div>
                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1 text-center mb-1" data-id="element-903">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => <div key={day} className="text-xs font-medium text-gray-500 py-1" data-id="element-904">
                          {day}
                        </div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center" data-id="element-905">
                      {/* Empty cells for days before the first day of month */}
                      {Array.from({
                    length: getFirstDayOfMonth(currentMonth.getFullYear(), currentMonth.getMonth())
                  }).map((_, index) => <div key={`empty-${index}`} className="h-8" data-id="element-906"></div>)}
                      {/* Days of the month */}
                      {Array.from({
                    length: getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth())
                  }).map((_, index) => {
                    const day = index + 1;
                    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                    const isSelected = selectedDate && date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
                    const isToday = new Date().toDateString() === date.toDateString();
                    return <button key={day} className={`h-8 rounded-full flex items-center justify-center text-sm
                              ${isSelected ? 'bg-blue-600 text-white' : isToday ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`} onClick={() => handleDateSelection(day)} data-id="element-907">
                            {day}
                          </button>;
                  })}
                    </div>
                  </div>
                  <div className="flex justify-end" data-id="element-908">
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={confirmDateSelection} data-id="element-909">
                      Confirm
                    </button>
                  </div>
                </div>
              </div>}
            {/* Requirements Modal */}
            {showRequirementsModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-910">
                <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-911">
                  <div className="flex justify-between items-center mb-4" data-id="element-912">
                    <h3 className="text-lg font-semibold" data-id="element-913">
                      Business Laptop Requirements
                    </h3>
                    <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowRequirementsModal(false)} data-id="element-914">
                      <XIcon size={20} data-id="element-915" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto" data-id="element-916">
                    <div className="space-y-4" data-id="element-917">
                      <section data-id="element-918">
                        <h4 className="font-medium text-gray-800 mb-2" data-id="element-919">
                          1. Main Components Functionality
                        </h4>
                        <div className="space-y-3 text-sm" data-id="element-920">
                          <p data-id="element-921">
                            <span className="font-medium" data-id="element-922">CPU/Processor:</span>{' '}
                            Should provide efficient multitasking, support
                            office productivity software, video conferencing,
                            and light data analysis. A modern multi-core
                            processor (e.g., Intel Core i5/i7 or AMD Ryzen 5/7)
                            is preferred.
                          </p>
                          <p data-id="element-923">
                            <span className="font-medium" data-id="element-924">Screen:</span> A
                            clear, anti-glare display with Full HD (1920×1080)
                            resolution for comfortable daily use. Screen size
                            between 14–15.6 inches is ideal for balancing
                            productivity and portability.
                          </p>
                          <p data-id="element-925">
                            <span className="font-medium" data-id="element-926">Audio Device:</span>{' '}
                            Integrated speakers and microphone suitable for
                            video calls and online meetings, with
                            noise-cancellation support recommended. A headphone
                            jack and Bluetooth audio support are desirable.
                          </p>
                          <p data-id="element-927">
                            <span className="font-medium" data-id="element-928">
                              Memory Space (RAM & Storage):
                            </span>{' '}
                            At least 16 GB RAM for smooth multitasking and a
                            minimum of 512 GB SSD storage for fast boot-up,
                            quick file access, and adequate storage of business
                            documents and applications.
                          </p>
                        </div>
                      </section>
                      <section data-id="element-929">
                        <h4 className="font-medium text-gray-800 mb-2" data-id="element-930">
                          2. Size and Weight
                        </h4>
                        <div className="space-y-3 text-sm" data-id="element-931">
                          <p data-id="element-932">
                            Slim and lightweight design to support portability
                            for travel and commuting.
                          </p>
                          <p data-id="element-933">Target weight: 1.2–1.8 kg.</p>
                          <p data-id="element-934">Thickness: less than 20 mm preferred.</p>
                        </div>
                      </section>
                      <section data-id="element-935">
                        <h4 className="font-medium text-gray-800 mb-2" data-id="element-936">
                          3. Warranty
                        </h4>
                        <div className="space-y-3 text-sm" data-id="element-937">
                          <p data-id="element-938">
                            A minimum of 3 years warranty covering parts and
                            labor.
                          </p>
                          <p data-id="element-939">
                            On-site or next-business-day support preferred for
                            reduced downtime.
                          </p>
                        </div>
                      </section>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-3" data-id="element-940">
                    <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" onClick={() => {
                  setShowRequirementsModal(false);
                  handleRequirementsConfirmation(false);
                }} data-id="element-941">
                      Make Changes
                    </button>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => {
                  setShowRequirementsModal(false);
                  handleRequirementsConfirmation(true);
                }} data-id="element-942">
                      Confirm Requirements
                    </button>
                  </div>
                </div>
              </div>}
            <div ref={messagesEndRef} data-id="element-943" />
          </div>
        </div>
        {/* Input Area */}
        <div className="border-t border-gray-200 p-3 bg-white" data-id="element-944">
          <div className="max-w-3xl mx-auto flex items-center bg-white border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500" data-id="element-945">
            <textarea className="flex-1 py-2 px-3 resize-none focus:outline-none min-h-[44px] max-h-[120px] rounded-l-lg" placeholder="Message Prospeum AI..." value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={handleKeyPress} rows={1} disabled={isAiTyping || currentWorkflow.stage === 'complete'} data-id="element-946" />
            <div className="flex items-center pr-2" data-id="element-947">
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-full" onClick={handleFileUpload} aria-label="Upload file" data-id="element-948">
                <PaperclipIcon size={20} data-id="element-949" />
              </button>
              <button className="p-2 text-blue-600 hover:text-blue-800 rounded-full disabled:text-gray-300" onClick={handleSendMessage} disabled={newMessage.trim() === '' || isAiTyping || currentWorkflow.stage === 'complete'} aria-label="Send message" data-id="element-950">
                <SendIcon size={20} data-id="element-951" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default SourcingAgent;